import { supabase } from "@/integrations/supabase/client";

const SESSION_KEY = "sercom_session_id";
const VISITOR_KEY = "sercom_visitor_id";
const VISITOR_FIRST_SEEN_KEY = "sercom_visitor_first_seen";

function getSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return "anon";
  }
}

function getVisitorInfo(): { id: string; isNew: boolean } {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    let isNew = false;
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(VISITOR_KEY, id);
      localStorage.setItem(VISITOR_FIRST_SEEN_KEY, new Date().toISOString());
      isNew = true;
    }
    return { id, isNew };
  } catch {
    return { id: "anon", isNew: false };
  }
}

// Kept for backwards compat (no UI banner anymore — tracking always on).
export function getConsent(): "granted" | "denied" | null {
  return "granted";
}
export function setConsent(_value: "granted" | "denied") {}

function detectDevice(): string {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent.toLowerCase();
  if (/ipad|tablet/.test(ua)) return "tablet";
  if (/mobile|iphone|android/.test(ua)) return "mobile";
  return "desktop";
}

function detectSource(referrer: string): string {
  if (!referrer) return "direct";
  try {
    const host = new URL(referrer).hostname.replace(/^www\./, "");
    if (host === window.location.hostname) return "internal";
    if (host.includes("lovable")) return "lovable_preview";
    if (host.includes("instagram")) return "instagram";
    if (host.includes("facebook") || host.includes("fb.")) return "facebook";
    if (host.includes("google")) return "google";
    if (host.includes("bing")) return "bing";
    if (host.includes("yahoo")) return "yahoo";
    if (host.includes("duckduckgo")) return "duckduckgo";
    if (host.includes("youtube") || host.includes("youtu.be")) return "youtube";
    if (host.includes("tiktok")) return "tiktok";
    if (host.includes("twitter") || host === "t.co" || host.includes("x.com")) return "twitter";
    if (host.includes("linkedin") || host === "lnkd.in") return "linkedin";
    if (host.includes("whatsapp") || host === "wa.me") return "whatsapp";
    if (host.includes("t.me") || host.includes("telegram")) return "telegram";
    if (host.includes("idealista")) return "idealista";
    if (host.includes("fotocasa")) return "fotocasa";
    if (host.includes("habitaclia")) return "habitaclia";
    return host;
  } catch {
    return "direct";
  }
}

async function send(event: {
  event_type: "first_visit" | "pageview" | "whatsapp_click" | "call_click" | "email_click";
  page?: string;
  property_id?: string;
}) {
  try {
    const host = window.location.hostname;
    if (host === "localhost" || host.startsWith("127.")) return;
    if (window.location.pathname.startsWith("/admin")) return;
  } catch {
    return;
  }

  const referrer = typeof document !== "undefined" ? document.referrer : "";
  const visitor = getVisitorInfo();
  const payload = {
    ...event,
    page: event.page ?? window.location.pathname,
    referrer,
    source: detectSource(referrer),
    device: detectDevice(),
    session_id: getSessionId(),
    visitor_id: visitor.id,
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
  };

  try {
    if (event.event_type === "pageview" && visitor.isNew) {
      await supabase.from("analytics_events").insert({ ...payload, event_type: "first_visit" });
    }
    await supabase.from("analytics_events").insert(payload);
  } catch (err) {
    console.debug("analytics error", err);
  }
}

export function trackPageview(path?: string) {
  void send({ event_type: "pageview", page: path });
}

export function trackWhatsAppClick(propertyId?: string) {
  void send({ event_type: "whatsapp_click", property_id: propertyId });
}

export function trackCallClick(propertyId?: string) {
  void send({ event_type: "call_click", property_id: propertyId });
}

export function trackEmailClick(propertyId?: string) {
  void send({ event_type: "email_click", property_id: propertyId });
}
